import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Edit, Trash2, Plus } from 'lucide-react'
import FbButton from 'components/FbUI/FbButton'
import { Select as UISelect } from 'components/UI/Select/Select'
import type { ISelectItem } from 'components/UI/Select/Select'
import { ProductSearchModal } from '../ProductSearchModal'
import { VariantSelectButton } from '../VariantSelectButton'
import type { ProductVariantSelection } from '../ProductSearchModal/types'
import type { Product } from 'models/product'
import {
  mapVariantToDisplay,
  findProductById,
  hasProductNoVariants,
} from '../../../utils/productHelpers'

/**
 * Represents a selected product with its variants
 */
export interface ProductWithVariants {
  productId: number
  productName: string
  variantIds: number[]
  variants: Array<{
    id: number
    name: string
    format_pack_size?: string | null
    avg_price?: number | null
    sku?: string
  }>
}

/**
 * Props for ProductVariantSelector component
 */
export interface ProductVariantSelectorProps {
  // Available products to select from
  products: Product[] | undefined
  isLoadingProducts?: boolean

  // Current selections
  selections: ProductWithVariants[]

  // Callbacks
  onAdd: (selection: ProductWithVariants) => void
  onEdit: (index: number, selection: ProductWithVariants) => void
  onRemove: (index: number) => void

  // Configuration
  multipleProducts?: boolean // Default: true (allow multiple products). False = single product only
  multipleVariants?: boolean // Default: true (allow multiple variants per product). False = single variant
  showVariantList?: boolean // Default: true (show list of variants in cards). False = show count only
  optionalVariants?: boolean // Default: false (variants required). True = allow product without variant selection

  // Labels & Text
  selectorLabel?: string // Default: "Select Product"
  selectButtonText?: string // Default: "Select Variants"
  emptyStateText?: string // Default: "No products added yet. Select a product above and choose its variants to get started."

  // Optional custom rendering for variant display
  renderVariant?: (
    variant: ProductWithVariants['variants'][0]
  ) => React.ReactNode

  // Optional rendering for additional content below the variant list (e.g., quantity/format fields)
  renderAdditionalContent?: (
    selection: ProductWithVariants,
    index: number
  ) => React.ReactNode
}

/**
 * Shared ProductVariantSelector Component
 *
 * Used for selecting products and their variants in both:
 * - Rebates: Multiple products, multiple variants each
 * - Samples: Single product, single variant
 *
 * @example
 * // Rebates usage (multiple products, multiple variants)
 * <ProductVariantSelector
 *   products={products}
 *   selections={selectedProductsWithVariants}
 *   onAdd={handleAdd}
 *   onEdit={handleEdit}
 *   onRemove={handleRemove}
 *   multipleProducts={true}
 *   multipleVariants={true}
 * />
 *
 * @example
 * // Samples usage (single variant selection)
 * <ProductVariantSelector
 *   products={products}
 *   selections={selectedVariant ? [selectedVariant] : []}
 *   onAdd={handleAdd}
 *   onEdit={handleEdit}
 *   onRemove={handleRemove}
 *   multipleProducts={false}
 *   multipleVariants={false}
 *   showVariantList={false}
 * />
 */
export function ProductVariantSelector({
  products,
  isLoadingProducts = false,
  selections,
  onAdd,
  onEdit,
  onRemove,
  multipleProducts = true,
  multipleVariants = true,
  showVariantList = true,
  optionalVariants = false,
  selectorLabel = 'Select Product',
  selectButtonText = 'Select Variants',
  emptyStateText = 'No products added yet. Select a product above and choose its variants to get started.',
  renderVariant,
  renderAdditionalContent,
}: ProductVariantSelectorProps) {
  const navigate = useNavigate()
  const [selectedProductIdForModal, setSelectedProductIdForModal] = useState<
    string | null
  >(null)
  const [showVariantModal, setShowVariantModal] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)

  // Convert products to UISelect items
  const productItems: ISelectItem[] = useMemo(
    () =>
      products?.map((p) => ({
        key: p.id.toString(),
        label: p.name,
        value: p.id.toString(),
      })) || [],
    [products]
  )

  // Handle variant selection from modal
  const handleVariantSelect = (selection: ProductVariantSelection) => {
    // Handle empty selection during edit - remove the product
    if (
      editingIndex !== null &&
      (!selection.variants || selection.variants.length === 0)
    ) {
      onRemove(editingIndex)
      setEditingIndex(null)
      setSelectedProductIdForModal(null)
      setShowVariantModal(false)
      return
    }

    // For new additions, require at least one variant (unless optionalVariants is true)
    if (
      !optionalVariants &&
      (!selection.variants || selection.variants.length === 0)
    ) {
      return
    }

    const selectedProductId = selection.variants?.[0]?.product_id
    if (!selectedProductId) return

    const selectedProduct = findProductById(products, selectedProductId)
    if (!selectedProduct) return

    const productWithVariants: ProductWithVariants = {
      productId: selectedProduct.id,
      productName: selectedProduct.name,
      variantIds: selection.variants?.map((v) => v.id) || [],
      variants: selection.variants?.map(mapVariantToDisplay) || [],
    }

    if (editingIndex !== null) {
      onEdit(editingIndex, productWithVariants)
      setEditingIndex(null)
    } else {
      onAdd(productWithVariants)
    }

    setSelectedProductIdForModal(null)
    setShowVariantModal(false)
  }

  // Handle opening variant modal
  const handleOpenVariantModal = () => {
    if (!selectedProductIdForModal) return

    // Check if product has no variants
    if (hasProductNoVariants(products, selectedProductIdForModal)) {
      // Don't open modal, product has no variants
      return
    }

    setEditingIndex(null)
    setShowVariantModal(true)
  }

  // Handle edit variant modal
  const handleEditVariants = (index: number) => {
    const selection = selections[index]
    setEditingIndex(index)
    setSelectedProductIdForModal(selection.productId.toString())
    setShowVariantModal(true)
  }

  // Build initial selection for editing
  const initialSelectionForEdit = useMemo(() => {
    if (editingIndex === null) return undefined

    const selection = selections[editingIndex]
    if (!selection) return undefined

    // Convert ProductWithVariants to ProductVariantSelection for variants-only mode
    return {
      mode: 'variants-only' as const,
      variants: selection.variants.map((v) => ({
        id: v.id,
        name: v.name,
        sku: v.sku || '',
        gtin: null,
        format_pack_size: v.format_pack_size ?? null,
        product_id: selection.productId,
        product_name: selection.productName,
        distributor_codes: [],
        avg_price: v.avg_price ?? null,
      })),
    }
  }, [editingIndex, selections])

  // Check if selected product has no variants
  const hasNoVariants = hasProductNoVariants(
    products,
    selectedProductIdForModal
  )

  return (
    <div className="space-y-4">
      {/* Product Selection Section */}
      {(multipleProducts || selections.length === 0) && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
          {/* Product Dropdown - Full Width */}
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {selectorLabel}
            </label>
            <UISelect
              items={productItems}
              selected={selectedProductIdForModal}
              onSelect={(value) => setSelectedProductIdForModal(value)}
              nullSelectionLabel="Choose a product..."
              buttonVariant="secondary"
              buttonClassName="w-full"
              searchPlaceholder="Search products..."
              disabled={isLoadingProducts}
              matchTriggerWidth
            />
          </div>

          {/* Add Product/Variant Buttons */}
          {selectedProductIdForModal && (
            <>
              {/* Product has variants - show both options */}
              {!hasNoVariants && (
                <div className="flex gap-2">
                  <VariantSelectButton
                    onClick={handleOpenVariantModal}
                    text={selectButtonText}
                  />
                  {optionalVariants && (
                    <button
                      type="button"
                      onClick={() => {
                        const selectedProduct = findProductById(
                          products,
                          selectedProductIdForModal
                        )
                        if (selectedProduct) {
                          onAdd({
                            productId: selectedProduct.id,
                            productName: selectedProduct.name,
                            variantIds: [],
                            variants: [],
                          })
                          setSelectedProductIdForModal(null)
                        }
                      }}
                      className="flex-1 flex items-center gap-2 px-3 py-2 rounded-md border-[1px] border-brand-hover bg-surface-hover hover:bg-surface-hover-deep transition-colors"
                    >
                      <Plus size={16} className="text-brand" />
                      <span className="font-medium text-sm leading-none text-brand">
                        Add Product Only
                      </span>
                    </button>
                  )}
                </div>
              )}

              {/* Product has NO variants */}
              {hasNoVariants && (
                <>
                  {optionalVariants ? (
                    // optionalVariants=true: Allow adding product without variants
                    <button
                      type="button"
                      onClick={() => {
                        const selectedProduct = findProductById(
                          products,
                          selectedProductIdForModal
                        )
                        if (selectedProduct) {
                          onAdd({
                            productId: selectedProduct.id,
                            productName: selectedProduct.name,
                            variantIds: [],
                            variants: [],
                          })
                          setSelectedProductIdForModal(null)
                        }
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-md border-[1px] border-brand-hover bg-surface-hover hover:bg-surface-hover-deep transition-colors"
                    >
                      <Plus size={16} className="text-brand" />
                      <span className="font-medium text-sm leading-none text-brand">
                        Add Product
                      </span>
                    </button>
                  ) : (
                    // optionalVariants=false: Show warning
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                      <p className="text-yellow-800 text-sm">
                        This product has no variants. Please add variants in the
                        product settings before selecting it.
                      </p>
                      <FbButton
                        onClick={() => navigate('/products')}
                        className="mt-2"
                        size="sm"
                      >
                        Go to Product Settings
                      </FbButton>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Empty State */}
      {selections.length === 0 && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center text-gray-500">
          {emptyStateText}
        </div>
      )}

      {/* Selected Products List */}
      {selections.map((selection, index) => (
        <div
          key={`${selection.productId}-${index}`}
          className="border border-gray-200 rounded-lg p-4 bg-white"
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h4 className="font-medium text-gray-900">
                {selection.productName}
              </h4>
              <p className="text-sm text-gray-500 mt-1">
                {selection.variants.length === 0
                  ? 'Product only (no specific variant)'
                  : `${selection.variants.length} variant${selection.variants.length !== 1 ? 's' : ''} selected`}
              </p>
            </div>
            <div className="flex gap-3 items-center">
              <button
                type="button"
                onClick={() => handleEditVariants(index)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
                title="Edit Variants"
              >
                <Edit size={18} />
              </button>
              <button
                type="button"
                onClick={() => onRemove(index)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
                title="Remove"
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>

          {/* Variant List (optional) */}
          {showVariantList && selection.variants.length > 0 && (
            <div className="mt-2 space-y-1">
              {selection.variants.map((variant) => (
                <div
                  key={variant.id}
                  className="text-sm text-gray-600 pl-3 border-l-2 border-gray-200"
                >
                  {renderVariant ? (
                    renderVariant(variant)
                  ) : (
                    <>
                      {variant.name}
                      {variant.format_pack_size && (
                        <span className="ml-2 text-gray-400">
                          ({variant.format_pack_size})
                        </span>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Additional content (e.g., quantity/format fields for Samples) */}
          {renderAdditionalContent && renderAdditionalContent(selection, index)}
        </div>
      ))}

      {/* Variant Search Modal */}
      <ProductSearchModal
        show={showVariantModal}
        onHide={() => {
          setShowVariantModal(false)
          setEditingIndex(null)
          setSelectedProductIdForModal(null)
        }}
        selectionMode="variants-only"
        onSelect={handleVariantSelect}
        title={editingIndex !== null ? 'Edit Variants' : 'Select Variants'}
        showProductFilter={false}
        multiSelect={multipleVariants}
        maxSelections={multipleVariants ? 100 : 1}
        restrictToProduct={
          selectedProductIdForModal
            ? Number(selectedProductIdForModal)
            : undefined
        }
        initialSelection={initialSelectionForEdit}
      />
    </div>
  )
}
